import * as t from 'io-ts';
import {chain, map} from 'lodash';

import {makeApiType, makeEnumRuntimeType, RContactUidRef} from './apiTypes';
import {buildContactUid, ContactSourcesEnum, parseContactSource} from './contactModel';

/*
 * Runtime type.
 */

export enum RecipientMatchTypesEnum {
  CONTACT = 'contact',
  GROUP = 'group',
}

const RRecipientContactMatchBase = t.type({
  type: t.literal(RecipientMatchTypesEnum.CONTACT),
  uid: RContactUidRef,
  source: makeEnumRuntimeType(ContactSourcesEnum),
  handle: t.string,
  name: t.string,
});

const RRecipientGroupMatchBase = t.type({
  type: t.literal(RecipientMatchTypesEnum.GROUP),
  id: t.Integer,
  name: t.string,
  contactsCount: t.union([t.number, t.undefined]),
});

const RRecipientContactMatchDirty = t.readonly(RRecipientContactMatchBase);
const RRecipientGroupMatchDirty = t.readonly(RRecipientGroupMatchBase);

const RRecipientMatchDirty = t.readonly(
  t.taggedUnion('type', [RRecipientContactMatchBase, RRecipientGroupMatchBase]),
);

/*
 * Type.
 */

export interface RecipientContactMatchModel extends t.TypeOf<typeof RRecipientContactMatchDirty> {}
export interface RecipientGroupMatchModel extends t.TypeOf<typeof RRecipientGroupMatchDirty> {}
export type RecipientMatchModel = RecipientContactMatchModel | RecipientGroupMatchModel;

export const RRecipientContactMatch = t.clean<RecipientContactMatchModel>(RRecipientContactMatchDirty as any);
export const RRecipientGroupMatch = t.clean<RecipientGroupMatchModel>(RRecipientGroupMatchDirty as any);
export const RRecipientMatch = t.clean<RecipientMatchModel>(RRecipientMatchDirty as any);

const RRecipientContactMatches = t.readonlyArray(RRecipientContactMatch);
const RRecipientMatches = t.readonlyArray(RRecipientMatch);

/*
 * IO methods.
 */

export function parseRecipientMatchContact(src: any): RecipientContactMatchModel {
  return {
    type: RecipientMatchTypesEnum.CONTACT,
    uid: buildContactUid(src.source, src.handle),
    source: parseContactSource(src.source),
    handle: src.handle,
    name: src.name.trim(),
  };
}

export function parseRecipientMatch(src: any): RecipientMatchModel {
  switch (src.type) {
    case RecipientMatchTypesEnum.CONTACT:
      return parseRecipientMatchContact(src);

    case RecipientMatchTypesEnum.GROUP:
    default:
      return {
        type: src.type,
        id: src.id,
        name: src.name,
        contactsCount: src.contacts_count,
      };
  }
}

export function parseRecipientContactMatches(src: any): ReadonlyArray<RecipientContactMatchModel> {
  return chain(src)
    .filter((m) => m.type === RecipientMatchTypesEnum.CONTACT)
    .map(parseRecipientMatchContact)
    .value();
}

export function parseRecipientMatches(src: any): ReadonlyArray<RecipientMatchModel> {
  return map(src, parseRecipientMatch);
}

/*
 * API type.
 */

export const ApiRecipientMatch = makeApiType('recipientMatch', RRecipientMatch, parseRecipientMatch);
export const ApiRecipientContactMatches = makeApiType(
  'recipientContactMatches',
  RRecipientContactMatches,
  parseRecipientContactMatches,
);
export const ApiRecipientMatches = makeApiType('recipientMatches', RRecipientMatches, parseRecipientMatches);
